import { useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/data-display/page-header";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { LoadingState } from "@/components/ui/system-state";
import { ProviderMigrationNav } from "@/features/provider-migration/journey-nav";
import { InfoBanner } from "@/components/ui/banner";
import { UnavailableWithRetry } from "@/features/provider-migration/shared";
import { buildEvidencePacket, type EvidencePacket } from "@/features/provider-migration/evidence";
import { useProviderMigrationSummary } from "@/hooks/use-provider-migration";

/**
 * Provider migration, step 4: export evidence (page spec provider-migration-export-evidence).
 * Builds the evidence packet in the browser from the real sealed snapshot the gateway returned.
 * Identifiers only: no credentials, no provider attribute map, no balances.
 */
export default function ProviderMigrationExportEvidencePage() {
  const s = useProviderMigrationSummary();
  const [generation, setGeneration] = useState<{ identity: string; packet: EvidencePacket } | null>(null);
  const categories = [s.vaults, s.wallets, s.addresses];
  // Compute from values on every render: inventory rows may be mutated in place.
  const ready = !s.loading && !s.error && typeof s.snapshotId === "string" &&
    s.snapshotId.trim().length > 0 && categories.every((result) =>
      !result.loading && !result.error && result.data != null &&
      result.data.snapshotId === s.snapshotId,
    );
  const input = {
    snapshotId: s.snapshotId ?? "",
    freshUntil: s.freshUntil,
    truncated: s.truncated,
    vaultAccounts: s.vaults.data?.items ?? [],
    assetWallets: s.wallets.data?.items ?? [],
    vaultAddresses: s.addresses.data?.items ?? [],
  };
  const identity = ready ? JSON.stringify({
    // Exclude generation time and all provider attributes from the read identity.
    observation: buildEvidencePacket({ ...input, generatedAt: "" }),
    categories: categories.map(({ data }) => ({
      present: data != null,
      category: data?.category,
      snapshotId: data?.snapshotId,
      freshUntil: data?.freshUntil,
      truncated: data?.truncated,
    })),
  }) : null;
  const packet = generation && generation.identity === identity ? generation.packet : null;
  // Retire during render, before exposing controls, so A → B → A cannot revive A.
  if (generation && generation.identity !== identity) setGeneration(null);

  const generate = () => {
    if (identity === null) return;
    setGeneration({ identity, packet: buildEvidencePacket({ ...input, generatedAt: new Date().toISOString() }) });
  };
  const refresh = () => {
    setGeneration(null);
    s.refresh();
  };

  const download = () => {
    if (!packet) return;
    const blob = new Blob([JSON.stringify(packet, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provider-migration-evidence-${packet.snapshotId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader
        title="Export evidence"
        subtitle="A read-only record of what discovery found, for audit."
      />
      <ProviderMigrationNav active="export" />
      <InfoBanner variant="info" title="Read-only, no funds moved">
        This page records source observations only. It cannot approve a plan, establish canary readiness, or move funds.
      </InfoBanner>

      {s.loading && <LoadingState rows={3} />}
      {!s.loading && s.error && (
        <UnavailableWithRetry title="Evidence is not available" error={s.error} onRetry={refresh} />
      )}
      {!s.loading && !s.error && !s.snapshotId && (
        <div className="max-w-2xl">
          <EmptyState
            card
            title="No sealed snapshot yet"
            description="Seal a discovery snapshot first; there is nothing to export until then."
            action={
              <Link
                to="/reconciliation/provider-migration/discovery"
                className={buttonVariants({ size: "md" })}
              >
                Go to Discovery
              </Link>
            }
          />
        </div>
      )}
      {!s.loading && !s.error && s.snapshotId && (
        <div className="max-w-2xl space-y-4">
          <p className="text-sm text-ink">
            {s.total} discovered items in snapshot <span className="break-all font-mono text-xs">{s.snapshotId}</span>.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="md" onClick={generate} disabled={!ready}>
              Generate evidence packet
            </Button>
            <Button size="md" variant="secondary" onClick={download} disabled={!packet}>
              Download JSON
            </Button>
            <Button size="md" variant="secondary" onClick={refresh}>
              Refresh inventory
            </Button>
          </div>
          {packet && (
            <pre
              aria-label="Evidence packet preview"
              className="max-h-72 overflow-auto rounded-lg border border-line bg-surface p-3 text-xs text-ink"
            >
              {JSON.stringify({ ...packet, items: `[${packet.counts.total} items in download]` }, null, 2)}
            </pre>
          )}
          <p className="text-xs text-muted">
            Generated in your browser from the snapshot the gateway returned. Historical freshness and truncated discovery
            remain recorded in the packet. Only resource identifiers are included; no credentials are exported.
          </p>
        </div>
      )}
    </div>
  );
}
